"use client";

import type { AssessmentNextStep, AssessmentOption, AssessmentResult, AssessmentTier } from "@/lib/gamification/assessment-engine";

export type AssessmentResultCardCopy = {
  readonly scoreLabel: string;
  readonly statusLabel: string;
  readonly gapsLabel: string;
  readonly nextStepsLabel: string;
  readonly exportLabel?: string;
  readonly exportHref?: string;
};

type AssessmentResultLike = {
  readonly tier: AssessmentTier;
  readonly scorePercent: number;
  readonly topGaps?: readonly AssessmentOption[];
  readonly nextSteps?: readonly AssessmentNextStep[];
};

type AssessmentResultCardProps = {
  readonly copy: AssessmentResultCardCopy;
  readonly result: AssessmentResult | AssessmentResultLike;
  readonly id: string;
  readonly onExportIntent?: () => void;
};

export default function AssessmentResultCard({ copy, id, onExportIntent, result }: AssessmentResultCardProps) {
  const gaps = result.topGaps ?? [];
  const nextSteps = result.nextSteps ?? [];

  return (
    <article className="gamification-tool__result redesign-list-card" aria-labelledby={`${id}-result-title`} aria-live="polite">
      <p className="marketing-eyebrow">{copy.statusLabel}</p>
      <h3 id={`${id}-result-title`}>{result.tier.label}</h3>
      <p>{result.tier.summary}</p>
      <dl className="gamification-tool__result-stats">
        <div>
          <dt>{copy.scoreLabel}</dt>
          <dd>{result.scorePercent}%</dd>
        </div>
        {result.tier.statusBadge ? (
          <div>
            <dt>{copy.statusLabel}</dt>
            <dd>{result.tier.statusBadge}</dd>
          </div>
        ) : null}
      </dl>

      {gaps.length > 0 ? (
        <div className="gamification-tool__result-block">
          <h4>{copy.gapsLabel}</h4>
          <ul>
            {gaps.map((gap) => (
              <li key={gap.id}>{gap.label}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {nextSteps.length > 0 ? (
        <div className="gamification-tool__result-block">
          <h4>{copy.nextStepsLabel}</h4>
          <ul>
            {nextSteps.map((step) => (
              <li key={step.id}>
                {step.href ? <a href={step.href}>{step.label}</a> : step.label}
                {step.description ? <span>{step.description}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {copy.exportLabel && copy.exportHref ? (
        <a className="marketing-button marketing-button--secondary" href={copy.exportHref} onClick={onExportIntent}>
          {copy.exportLabel}
        </a>
      ) : null}

      {copy.exportLabel && !copy.exportHref && onExportIntent ? (
        <button type="button" className="marketing-button marketing-button--secondary" onClick={onExportIntent}>
          {copy.exportLabel}
        </button>
      ) : null}
    </article>
  );
}
